/**
 * useManifest hook.
 *
 * The frontend mirror of `manifest.context.get_current_manifest` on the
 * backend. Used everywhere a feature/module is conditionally rendered.
 *
 * Usage:
 *   const { manifest, isLoading } = useManifest();
 *   if (manifest?.modules_enabled.scheduling_external) { ... }
 *
 * Or the convenience hooks:
 *   const isAccountingOn = useModuleEnabled("accounting");
 *   const aiOn = useFeatureFlag("discovery_copilot");
 *   const donorLabel = useFieldLabel("person.role.donor", "Donor");
 */

import { useIsAuthenticated } from "@/features/auth/model/useAuth";

import { resolveTenantSlug } from "@/shared/lib/tenantSlug";

import { useGetCurrentManifestQuery } from "../api/manifestApi";

import { shouldFetchManifest } from "./shouldFetchManifest";
import type { Manifest, ModulesEnabled } from "./types";

/** Hook: the current tenant's manifest (frontend mirror of get_current_manifest). */
export function useManifest(): { manifest: Manifest | undefined; isLoading: boolean } {
  const isAuthenticated = useIsAuthenticated();
  const urlTenantSlug = resolveTenantSlug();
  const enabled = shouldFetchManifest({ urlTenantSlug, isAuthenticated });
  const { data, isLoading } = useGetCurrentManifestQuery(undefined, { skip: !enabled });
  return { manifest: data, isLoading: enabled ? isLoading : false };
}

/** Hook: true if the named module is enabled in the tenant's manifest. */
export function useModuleEnabled(moduleName: keyof ModulesEnabled): boolean {
  const { manifest } = useManifest();
  if (!manifest) return false;
  return Boolean(manifest.modules_enabled[moduleName]);
}

/** Hook: true if either scheduling module flag is enabled. */
export function useSchedulingEnabled(): boolean {
  const { manifest } = useManifest();
  if (!manifest) return false;
  const modules = manifest.modules_enabled;
  return Boolean(modules.scheduling_internal || modules.scheduling_external);
}

/** Hook: true if the named manifest feature flag is on. */
export function useFeatureFlag(flag: string): boolean {
  const { manifest } = useManifest();
  if (!manifest) return false;
  return Boolean(manifest.feature_flags[flag]);
}

/** Hook: the tenant's configured label for a field (manifest-driven copy). */
export function useFieldLabel(key: string, fallback: string): string {
  const { manifest } = useManifest();
  if (!manifest) return fallback;
  return manifest.field_labels[key] ?? fallback;
}

/** Hook: the tenant's branding (colours, logo) from the manifest. */
export function useTenantBranding() {
  const { manifest } = useManifest();
  return manifest?.branding;
}
